/**
 * Kontrola struktury zálohy před obnovou – čistá funkce bez databáze.
 *
 * Obnova nahradí všechna data, proto se poškozený nebo ručně upravený soubor
 * musí odmítnout dřív, než se cokoli smaže. Kontrolují se jen údaje, které
 * aplikace zapisuje odjakživa (od schématu 1) – starší zálohy tak projdou vždy.
 */
import { BACKUP_FORMAT, SCHEMA_VERSION } from './model';

const STORES = ['apiaries', 'colonies', 'queens', 'events', 'products', 'settings'] as const;
const NAMES: Record<(typeof STORES)[number], string> = {
  apiaries: 'stanoviště',
  colonies: 'včelstvo',
  queens: 'matka',
  events: 'zápis',
  products: 'přípravek',
  settings: 'nastavení',
};

const isObj = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x);
const isStr = (x: unknown): x is string => typeof x === 'string';
const isId = (x: unknown) => isStr(x) && x.length > 0;

/** Co je na řádku zálohy špatně (nebo null, když je v pořádku). */
function rowProblem(store: (typeof STORES)[number], r: unknown): string | null {
  if (!isObj(r)) return 'není to záznam';
  if (store === 'settings') return isId(r.key) ? null : 'chybí klíč';
  if (!isId(r.id)) return 'chybí identifikátor';
  switch (store) {
    case 'apiaries':
      return isStr(r.name) ? null : 'chybí název';
    case 'products':
      return isStr(r.name) ? null : 'chybí název';
    case 'colonies':
      if (!isStr(r.label)) return 'chybí číslo úlu';
      // stanoviště může chybět: verze do 0.9.1 uložila přesun bez cíle jako prázdné stanoviště –
      // taková záloha musí jít obnovit
      return r.apiaryId === undefined || r.apiaryId === null || isStr(r.apiaryId) ? null : 'neplatné stanoviště';
    case 'events':
      if (!isId(r.type)) return 'chybí druh zápisu';
      if (!isStr(r.date) || !/^\d{4}-\d{2}-\d{2}$/.test(r.date)) return 'chybí nebo je neplatné datum';
      if (!Array.isArray(r.colonyIds) || !r.colonyIds.every(isStr)) return 'chybí seznam včelstev';
      return null;
    default:
      return null;
  }
}

/**
 * Vrátí českou hlášku, proč zálohu nejde obnovit, nebo null, když je v pořádku.
 * Data v telefonu se při chybě nezmění.
 */
export function backupProblem(backup: unknown): string | null {
  if (!isObj(backup) || backup.format !== BACKUP_FORMAT) return 'Soubor není záloha včelařské aplikace.';
  const v = backup.schemaVersion;
  if (typeof v !== 'number' || !Number.isInteger(v) || v < 1) return 'Záloha je poškozená – chybí verze dat.';
  if (v > SCHEMA_VERSION) return 'Záloha pochází z novější verze aplikace – nejdřív aplikaci aktualizujte.';
  if (!isObj(backup.data)) return 'Záloha je poškozená – neobsahuje žádná data.';
  const data = backup.data;
  for (const s of STORES) {
    const rows = data[s];
    if (rows === undefined) continue;
    if (!Array.isArray(rows)) return `Záloha je poškozená – část „${NAMES[s]}“ nemá správný tvar.`;
    for (let i = 0; i < rows.length; i++) {
      const p = rowProblem(s, rows[i]);
      if (p) return `Záloha je poškozená (${NAMES[s]} č. ${i + 1}: ${p}). Data v telefonu zůstala beze změny.`;
    }
  }
  return null;
}
